/**
 * InactiveTabOverlay
 *
 * Full-screen blocking overlay shown in non-active browser tabs.
 * The user can press "Use This Tab" to reclaim leadership for the current tab,
 * which broadcasts TAB_RECLAIM to all other tabs, reloads the page so all
 * in-memory data (Redux state, pending API calls) is fully refreshed.
 *
 * Web-only: rendered conditionally via `Platform.OS === 'web'` in the host.
 */

import React, { useEffect, useState } from 'react';
import {
  Modal,
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Platform,
} from 'react-native';
import { tabManager } from '@shared/services/tabManager.web';

const InactiveTabOverlay: React.FC = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (Platform.OS !== 'web') return;

    // Show overlay when this tab loses leadership.
    const unsubInactive = tabManager.onInactive(() => {
      setVisible(true);
    });

    // Hide overlay when this tab reclaims leadership (e.g. user pressed CTA).
    const unsubActive = tabManager.onActive(() => {
      setVisible(false);
    });

    // If this tab was already inactive when the component mounts
    // (e.g. hot-reload or fast-bootstrap scenario where PONG arrived before
    // this component mounted), reflect the current state immediately.
    if (!tabManager.isActive()) {
      setVisible(true);
      return () => {
        unsubInactive();
        unsubActive();
      };
    }

    // Also check after the PING_TIMEOUT_MS window (600ms) in case the PONG
    // arrived and set _isActive=false before our onInactive listener was
    // registered but after the mount-time isActive() check above ran.
    // This covers the race: tabManager.init() fires → PONG received →
    // _isActive=false → React mounts overlay → isActive() check passes
    // the false check above, but just in case the timing is marginal, we
    // check once more at 700ms (after the 600ms PING_TIMEOUT_MS window closes).
    const fallbackTimer = setTimeout(() => {
      if (!tabManager.isActive()) {
        setVisible(true);
      }
    }, 700);

    return () => {
      unsubInactive();
      unsubActive();
      clearTimeout(fallbackTimer);
    };
  }, []);

  if (!visible) return null;

  const handleUseThisTab = () => {
    tabManager.reclaimTab();
    // Reload so stale Redux state, pending API calls, and any other in-memory
    // data are fully refreshed — the tab starts clean as the new active tab.
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      testID="inactive-tab-modal"
      accessibilityViewIsModal
      onRequestClose={() => {
        // Prevent Android back-button from dismissing without reclaiming.
      }}>
      <View style={styles.backdrop} testID="inactive-tab-overlay">
        <View style={styles.card} accessibilityRole="alert">
          <Text style={styles.title} accessibilityRole="header">
            This Tab Is Inactive
          </Text>
          <Text style={styles.body}>
            You have this app open in another tab. Only one tab can be active at
            a time to prevent data conflicts.
          </Text>
          <TouchableOpacity
            style={styles.button}
            onPress={handleUseThisTab}
            accessibilityRole="button"
            accessibilityLabel="Use This Tab"
            accessibilityHint="Makes this tab the active tab and dismisses the inactive overlay"
            testID="use-this-tab-button">
            <Text style={styles.buttonText}>Use This Tab</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 32,
    maxWidth: 420,
    width: '90%',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
  },
  title: {
    fontSize: 20,
    fontWeight: '700',
    color: '#1A1A2E',
    marginBottom: 12,
    textAlign: 'center',
  },
  body: {
    fontSize: 15,
    lineHeight: 22,
    color: '#4A4A6A',
    textAlign: 'center',
    marginBottom: 28,
  },
  button: {
    backgroundColor: '#0057B8',
    borderRadius: 8,
    paddingVertical: 14,
    paddingHorizontal: 40,
  },
  buttonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
    textAlign: 'center',
  },
});

export default InactiveTabOverlay;